import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, Plus, Wallet } from 'lucide-react'
import {
  formatMoney, getProjectBillingConfig, topupProjectWallet,
  type ProjectBillingConfig,
} from '@/api/billing'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'

/**
 * The application's own MONEY wallet (INR/USD — never credits), funded by
 * whoever manages the project. Runtime seats, app downloads and registration
 * handling are the APPLICATION's cost, and keeping them in their own wallet
 * means one manager's personal balance never silently pays for another
 * project's users.
 */
export default function ProjectWalletCard({
  projectId, canManage, refreshKey,
}: {
  projectId: string
  canManage: boolean
  refreshKey?: number
}) {
  const { checkout, busy } = useRazorpayCheckout()
  const [config, setConfig] = useState<ProjectBillingConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [topUpOpen, setTopUpOpen] = useState(false)
  const [amount, setAmount] = useState('')

  const load = useCallback(async () => {
    try {
      setConfig(await getProjectBillingConfig(projectId))
    } catch {
      // A project whose billing has never been touched is not an error state —
      // the card simply doesn't render.
      setConfig(null)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => { void load() }, [load, refreshKey])

  const quickAmounts = config?.currency === 'INR' ? [500, 1000, 2500] : [10, 25, 50]

  const addFunds = useCallback(async () => {
    const major = parseFloat(amount)
    if (!(major > 0)) { toast.error('Enter an amount'); return }
    const amountMinor = Math.round(major * 100)
    const result = await checkout({
      purpose: 'project_wallet_topup',
      description: `Wallet top-up for ${projectId}`,
      precreate: () => topupProjectWallet(projectId, amountMinor),
    })
    if (result.cancelled) return
    if (!result.ok) {
      toast.error(result.error || 'The payment did not complete')
      return
    }
    toast.success('Funds added to this application')
    setTopUpOpen(false)
    setAmount('')
    await load()
  }, [amount, checkout, load, projectId])

  if (loading || !config) return null
  // Nothing to fund while billing is off — showing an inert wallet would just
  // raise questions.
  if (!config.credit_billing_enabled) return null

  const symbol = config.currency === 'INR' ? '₹' : '$'

  return (
    <>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
          background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
          borderRadius: 8, marginBottom: 16, flexWrap: 'wrap',
        }}
      >
        <Wallet size={18} style={{ color: 'var(--color-accent)' }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {config.wallet.display ?? formatMoney(config.wallet.balance, config.currency)}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            {config.effective_runtime_user_cost_minor > 0
              ? `${config.effective_runtime_user_cost_display} per runtime user`
              : 'Runtime users are free on this application'}
            {config.effective_pro_invite_cost_minor > 0 &&
              ` · ${config.effective_pro_invite_cost_display} for a Pro invitee`}
          </div>
        </div>
        {canManage && (
          <Button variant="secondary" onClick={() => setTopUpOpen(true)}>
            <Plus size={14} /> Add funds
          </Button>
        )}
      </div>

      {topUpOpen && (
        <Modal open onClose={() => setTopUpOpen(false)} title={`Add funds to ${projectId}`}>
          {busy ? (
            <div style={{ padding: 24, textAlign: 'center' }}><Loader2 className="spin" /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
                This wallet pays for runtime seats and app downloads, in {config.currency}.
                Tax is calculated at checkout.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {quickAmounts.map((q) => (
                  <button key={q} className="btn btn-secondary" onClick={() => setAmount(String(q))}>
                    {symbol}{q}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>{symbol}</span>
                <input
                  type="number" min={1} step="0.01" placeholder="Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  style={{ flex: 1 }}
                  autoFocus
                />
                <Button onClick={addFunds} disabled={busy || !(parseFloat(amount) > 0)}>
                  Add {parseFloat(amount) > 0 ? `${symbol}${parseFloat(amount)}` : 'funds'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  )
}
